import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PricingBand, PricingConfig, PricingDiscount, PricingLead, PricingPool, PricingSeason, ReservationType } from '@smile/shared';
import { POOL_LABEL, PRICELIST_DAYS, PRICING_POOLS, RESERVATION_TYPES } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { QuoteBreakdown } from '../components/QuoteBreakdown';
import { CZK, TYPE_LABEL, TYPE_SHORT } from '../lib/format';
import { todayISO } from '../lib/filters';
import { can } from '../lib/perms';

/**
 * Nastavení → Dynamické ceny — the owner's pricing: seasons with their own
 * price tables, filling bands per pool, lead-time steps, discounts, the
 * caps, and the switch that makes the desk price this way. Nothing here
 * changes what the web charges (that is Ceníky) until the web is pointed
 * at the new quote — which is why every quote shows the legacy price
 * beside the new one, and why the sandbox at the bottom compares them.
 */
function errText(err: unknown): string | null {
  if (err instanceof ApiError) return err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message);
  return err instanceof Error ? err.message : null;
}
function czDay(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${Number(d)}. ${Number(m)}. ${y}`;
}
const F = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

/* ---- the switch and the caps ---------------------------------------- */

function SettingsCard({ cfg, canWrite }: { cfg: PricingConfig; canWrite: boolean }) {
  const qc = useQueryClient();
  const s = cfg.settings;
  const [d, setD] = useState({
    engine: s.engine,
    roundTo: String(s.roundTo),
    maxFactor: String(s.maxFactor),
    minFactor: String(s.minFactor),
    capacityOutside: String(s.capacityOutside),
    capacityGarage: String(s.capacityGarage),
    capacityExclusive: String(s.capacityExclusive),
    overflow: s.overflow,
  });
  useEffect(() => {
    setD({
      engine: s.engine,
      roundTo: String(s.roundTo),
      maxFactor: String(s.maxFactor),
      minFactor: String(s.minFactor),
      capacityOutside: String(s.capacityOutside),
      capacityGarage: String(s.capacityGarage),
      capacityExclusive: String(s.capacityExclusive),
      overflow: s.overflow,
    });
  }, [s]);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['settings', 'pricing'] });
    await qc.invalidateQueries({ queryKey: ['quote'] });
  };
  const save = useMutation({
    mutationFn: () =>
      api.savePricingSettings({
        engine: d.engine,
        roundTo: Number(d.roundTo) === 1 ? 1 : 10,
        maxFactor: Number(d.maxFactor),
        minFactor: Number(d.minFactor),
        capacityOutside: Number(d.capacityOutside),
        capacityGarage: Number(d.capacityGarage),
        capacityExclusive: Number(d.capacityExclusive),
        overflow: d.overflow,
      }),
    onSuccess: refresh,
  });
  const imp = useMutation({ mutationFn: () => api.importPricing(), onSuccess: refresh });
  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useMutation({
    mutationFn: () => api.resetPricing(),
    onSuccess: async () => {
      setConfirmReset(false);
      imp.reset();
      await refresh();
    },
  });
  const dirty =
    d.engine !== s.engine ||
    Number(d.roundTo) !== s.roundTo ||
    Number(d.maxFactor) !== s.maxFactor ||
    Number(d.minFactor) !== s.minFactor ||
    Number(d.capacityOutside) !== s.capacityOutside ||
    Number(d.capacityGarage) !== s.capacityGarage ||
    Number(d.capacityExclusive) !== s.capacityExclusive ||
    d.overflow !== s.overflow;
  const message = errText(save.error) ?? errText(imp.error) ?? errText(reset.error);

  return (
    <form
      className="card detailcard"
      onSubmit={(e) => {
        e.preventDefault();
        if (canWrite && dirty && !save.isPending) save.mutate();
      }}
    >
      <h2>Který systém počítá cenu na přepážce</h2>
      {s.empty ? (
        <>
          <p className="settings-note">
            Nový systém zatím nemá žádnou sezónu. Import založí <b>základní sezónu</b> z ceníku #58, <b>zimní sezónu</b> pro každé období ceníku #19
            (zimní stání se tak počítá ze zimní tabulky podle data, ne podle kódu), <b>příčky obsazenosti</b> odpovídající dnešnímu žebříčku, návrh{' '}
            <b>předstihu</b> a jednu <b>slevu za každý promo kód</b>. Procento se bere z dohodnutého čísla v kódu CK, jinak z názvu ceníku, jinak se
            odvodí ze 7denní ceny Venku proti tabulce té sezóny, do které ceník patří. Každá sleva si nese <b>platnost svého ceníku</b>, takže kód mimo
            sezónu neplatí — a kód, který nikdo nikdy nepoužil, se založí rovnou <b>vyřazený</b>. Nic se tím nepřepne; přepínač je níž.
          </p>
          {canWrite && (
            <div className="efactions">
              <button type="button" className="btn primary" disabled={imp.isPending} onClick={() => imp.mutate()}>
                {imp.isPending ? 'Importuji…' : 'Importovat z ceníků'}
              </button>
            </div>
          )}
        </>
      ) : (
        <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="rolepick" role="radiogroup" aria-label="Cenový systém">
            <button type="button" role="radio" aria-checked={d.engine === 'legacy'} className={d.engine === 'legacy' ? 'btn sm primary' : 'btn sm'} onClick={() => setD({ ...d, engine: 'legacy' })}>
              Starý (ceníky — stejně jako web)
            </button>
            <button type="button" role="radio" aria-checked={d.engine === 'v2'} className={d.engine === 'v2' ? 'btn sm primary' : 'btn sm'} onClick={() => setD({ ...d, engine: 'v2' })}>
              Nový (sezóny × obsazenost × předstih)
            </button>
          </div>
          {d.engine === 'v2' && s.engine !== 'v2' && (
            <div className="notice" role="note" style={{ marginTop: 10 }}>
              Po uložení bude přepážka nabízet ceny z nového systému; web dál účtuje z ceníků. U každé ceny přepážka uvidí i to, co by řekl starý
              systém.
            </div>
          )}
          <h3 className="efh">Meze a zaokrouhlení</h3>
          <div className="efgrid">
            <label className="ef">
              Zaokrouhlit na
              <select value={d.roundTo} onChange={(e) => setD({ ...d, roundTo: e.target.value })}>
                <option value="10">10 Kč</option>
                <option value="1">1 Kč</option>
              </select>
            </label>
            <label className="ef">
              Nejvýš × (strop násobku)
              <input type="number" step={0.05} min={1} max={10} value={d.maxFactor} onChange={(e) => setD({ ...d, maxFactor: e.target.value })} />
            </label>
            <label className="ef">
              Nejméně × (podlaha)
              <input type="number" step={0.05} min={0.1} max={1} value={d.minFactor} onChange={(e) => setD({ ...d, minFactor: e.target.value })} />
            </label>
          </div>
          <h3 className="efh">Kapacity, proti kterým se měří obsazenost</h3>
          <div className="efgrid">
            <label className="ef">
              Venku
              <input type="number" min={1} max={5000} value={d.capacityOutside} onChange={(e) => setD({ ...d, capacityOutside: e.target.value })} />
            </label>
            <label className="ef">
              Garáž
              <input type="number" min={1} max={5000} value={d.capacityGarage} onChange={(e) => setD({ ...d, capacityGarage: e.target.value })} />
            </label>
            <label className="ef">
              Europort VIP
              <input type="number" min={1} max={5000} value={d.capacityExclusive} onChange={(e) => setD({ ...d, capacityExclusive: e.target.value })} />
            </label>
            <label className="ef">
              Přetékání
              <select value={d.overflow ? 'on' : 'off'} onChange={(e) => setD({ ...d, overflow: e.target.value === 'on' })}>
                <option value="on">venku + garáž dohromady (plný dvůr je plný pro oba)</option>
                <option value="off">každý produkt jen proti své kapacitě</option>
              </select>
            </label>
          </div>
          <p className="settings-note">
            Kapacity jsou to, co se dá prodat, ne co je na papíře: venku Tuchoměřice + Kněževes + POP venkovní, garáž Tuchoměřice + POP kryté,
            VIP Europort. Obsazenost se měří v nejtěsnější hodině celého pobytu, ne jen v den příjezdu. Při přetékání se u venkovního i
            garážového produktu bere horší z obou procent — vlastní pool a oba dohromady — protože na špičkové noci stojí venkovní auta
            v krytých místech (majitel, 28. 10. 2026).
          </p>
          <h3 className="efh">Import znovu</h3>
          <p className="settings-note">
            Smaže sezóny, jejich tabulky, oba žebříčky a všechny slevy a udělá import z ceníků znovu. Nastavení výš (přepínač, zaokrouhlení, meze,
            kapacity) zůstane. Jde to jen když počítá starý systém.
          </p>
          <div className="efactions">
            {!confirmReset ? (
              <button type="button" className="btn sm" disabled={s.engine === 'v2' || reset.isPending} onClick={() => setConfirmReset(true)}>
                Smazat nastavení a importovat znovu
              </button>
            ) : (
              <>
                <span className="empty">Opravdu smazat sezóny, žebříčky a slevy?</span>
                <button type="button" className="btn sm primary" disabled={reset.isPending} onClick={() => reset.mutate()}>
                  {reset.isPending ? 'Mažu…' : 'Ano, smazat'}
                </button>
                <button type="button" className="btn sm" disabled={reset.isPending} onClick={() => setConfirmReset(false)}>
                  Zpět
                </button>
              </>
            )}
            {reset.isSuccess && <span className="arrived-mark">✔ Smazáno — import nabídne karta výš</span>}
          </div>
        </fieldset>
      )}
      {/* Outside the empty/non-empty branch on purpose: the moment the import
          lands, `s.empty` flips and the branch above unmounts — the report
          used to vanish with it before anyone could read it. */}
      {imp.data && (
        <div className="notice" role="status">
          Importováno: základní sezóna #{imp.data.baseSeasonId}, {imp.data.winterSeasonIds.length} zimních sezón, {imp.data.discounts} slev z kódů,{' '}
          {imp.data.partners} partnerských. Z toho {imp.data.retired} rovnou vyřazených (nikdy nepoužité) a {imp.data.dated} s platností podle původního
          ceníku.
          {imp.data.skipped.length > 0 && <> Poznámky: {imp.data.skipped.join('; ')}.</>}
        </div>
      )}
      {!s.empty && (
        <div className="efactions">
          {canWrite ? (
            <button type="submit" className="btn primary" disabled={!dirty || save.isPending}>
              {save.isPending ? 'Ukládám…' : 'Uložit'}
            </button>
          ) : (
            <span className="empty">Ceny nastavuje majitel.</span>
          )}
          {save.isSuccess && !dirty && <span className="arrived-mark">✔ Uloženo</span>}
        </div>
      )}
      {message && (
        <div className="arrive-error" role="alert">
          {message}
        </div>
      )}
    </form>
  );
}

/* ---- seasons ---------------------------------------------------------- */

function SeasonsCard({ cfg, canWrite }: { cfg: PricingConfig; canWrite: boolean }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [from, setFrom] = useState('');
  const [till, setTill] = useState('');
  const [priority, setPriority] = useState('1');
  const [copyFrom, setCopyFrom] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const live = cfg.seasons.filter((s) => s.status === 'ok');
  const create = useMutation({
    mutationFn: () =>
      api.createSeason({
        title: title.trim(),
        validFrom: from || null,
        validTill: till || null,
        priority: Number(priority) || 0,
        copyFromId: copyFrom ? Number(copyFrom) : null,
      }),
    onSuccess: async () => {
      setTitle('');
      setFrom('');
      setTill('');
      await qc.invalidateQueries({ queryKey: ['settings', 'pricing'] });
    },
  });
  const ok = title.trim().length > 0 && (from === '') === (till === '') && (!from || from <= till);
  const rows = cfg.seasons.filter((s) => showRetired || s.status === 'ok');
  const day7 = (s: PricingSeason, t: ReservationType) => (s.rates[t] ? `${CZK.format(s.rates[t]![6])}` : '—');

  return (
    <div className="card detailcard">
      <h2>Sezóny</h2>
      <p className="settings-note">
        Sezóna je období s vlastní tabulkou cen (Venku / Garáž / Europort VIP podle délky pobytu). Cena se bere podle <b>dne příjezdu</b>; když se sezóny
        překrývají, vyhrává vyšší priorita. Sezóna bez dat je <b>základní</b> a platí, kdykoli žádná jiná. Produkt bez vlastní tabulky bere základní.
      </p>
      <table className="mini settings">
        <thead>
          <tr>
            <th>Sezóna</th>
            <th>Platí</th>
            <th className="num">Priorita</th>
            <th className="num">7 dní Venku</th>
            <th className="num">7 dní Garáž</th>
            <th className="num">7 dní Europort VIP</th>
            <th>Naposledy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className={s.status !== 'ok' ? 'deleted' : undefined}>
              <td>
                <b>
                  <Link to={`/nastaveni/ceny/sezona/${s.id}`}>{s.title}</Link>
                </b>
                {s.validFrom == null && <span className="stav gray" style={{ marginLeft: 6 }}>základní</span>}
              </td>
              <td>{s.validFrom ? `${czDay(s.validFrom)} – ${czDay(s.validTill)}` : <span className="was">vždy</span>}</td>
              <td className="num">{s.priority}</td>
              <td className="num">{day7(s, 'cheap')}</td>
              <td className="num">{day7(s, 'regular')}</td>
              <td className="num">{day7(s, 'exclusive')}</td>
              <td className="was">{s.updated ? `${s.updatedBy} · ${s.updated.slice(0, 16)}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="efactions">
        <label className="chk">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> i vyřazené
        </label>
      </div>
      {canWrite && (
        <form
          className="editform"
          style={{ marginTop: 10 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (ok && !create.isPending) create.mutate();
          }}
        >
          <div className="efgrid">
            <label className="ef">
              Nová sezóna
              <input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder="např. Léto 2027" required />
            </label>
            <label className="ef">
              Od (prázdné = základní)
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="ef">
              Do
              <input type="date" value={till} onChange={(e) => setTill(e.target.value)} />
            </label>
            <label className="ef">
              Priorita
              <input type="number" min={-100} max={100} value={priority} onChange={(e) => setPriority(e.target.value)} />
            </label>
            <label className="ef wide2">
              Zkopírovat ceny ze sezóny
              <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                <option value="">prázdná (doplníte v editoru)</option>
                {live.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="efactions">
            <button type="submit" className="btn primary" disabled={!ok || create.isPending}>
              {create.isPending ? 'Zakládám…' : 'Založit sezónu'}
            </button>
            {errText(create.error) && (
              <span className="arrive-error" role="alert">
                {errText(create.error)}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

/* ---- ladders (bands per pool, lead times) ------------------------------ */

type Step = { a: string; f: string };

function Ladder({
  title,
  note,
  aLabel,
  rows,
  onSave,
  canWrite,
}: {
  title: string;
  note: string;
  aLabel: string;
  rows: Array<{ a: number; f: number }>;
  onSave: (steps: Array<{ a: number; f: number }>) => Promise<unknown>;
  canWrite: boolean;
}) {
  const [d, setD] = useState<Step[]>(rows.map((r) => ({ a: String(r.a), f: String(r.f) })));
  useEffect(() => setD(rows.map((r) => ({ a: String(r.a), f: String(r.f) }))), [rows]);
  const m = useMutation({ mutationFn: () => onSave(d.map((s) => ({ a: Number(s.a), f: Number(s.f) }))) });
  const valid = d.every((s) => Number.isInteger(Number(s.a)) && Number(s.a) >= 0 && Number(s.f) > 0);
  const dirty = JSON.stringify(d) !== JSON.stringify(rows.map((r) => ({ a: String(r.a), f: String(r.f) })));
  return (
    <div className="ladder">
      <h3 className="efh">{title}</h3>
      <p className="settings-note">{note}</p>
      <table className="mini settings">
        <thead>
          <tr>
            <th className="num">{aLabel}</th>
            <th className="num">× násobek</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {d.map((s, i) => (
            <tr key={i}>
              <td className="num">
                <input type="number" min={0} step={1} value={s.a} disabled={!canWrite} onChange={(e) => setD(d.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))} />
              </td>
              <td className="num">
                <input type="number" min={0.1} max={10} step={0.05} value={s.f} disabled={!canWrite} onChange={(e) => setD(d.map((x, j) => (j === i ? { ...x, f: e.target.value } : x)))} />
              </td>
              <td className="act">
                {canWrite && (
                  <button type="button" className="btn sm" onClick={() => setD(d.filter((_, j) => j !== i))}>
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
          {d.length === 0 && (
            <tr>
              <td colSpan={3} className="empty">
                Žádná příčka — násobek 1.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {canWrite && (
        <div className="efactions">
          <button type="button" className="btn sm" onClick={() => setD([...d, { a: '', f: '1' }])}>
            + příčka
          </button>
          <button type="button" className="btn sm primary" disabled={!dirty || !valid || m.isPending} onClick={() => m.mutate()}>
            {m.isPending ? '…' : 'Uložit'}
          </button>
          {m.isSuccess && !dirty && <span className="arrived-mark">✔</span>}
          {errText(m.error) && (
            <span className="arrive-error" role="alert">
              {errText(m.error)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function LaddersCard({ cfg, canWrite }: { cfg: PricingConfig; canWrite: boolean }) {
  const qc = useQueryClient();
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['settings', 'pricing'] });
    await qc.invalidateQueries({ queryKey: ['quote'] });
  };
  const bandsOf = (pool: PricingPool) => cfg.bands.filter((b: PricingBand) => b.pool === pool).map((b) => ({ a: b.fromPct, f: b.factor }));
  return (
    <div className="card detailcard">
      <h2>Obsazenost a předstih</h2>
      <p className="settings-note">
        Obsazenost se měří v nejtěsnější hodině pobytu: kolik aut daného druhu stojí proti kapacitě (nahoře). Platí příčka s nejvyšším „od %“, které
        obsazenost dosáhla. Předstih je počet dní mezi objednáním a příjezdem; platí příčka s nejvyšším „od dní“, které předstih dosáhl. Výsledný
        násobek = obsazenost × předstih, omezený mezemi nahoře.
      </p>
      <div className="ladders">
        {PRICING_POOLS.map((pool) => (
          <Ladder
            key={pool}
            title={POOL_LABEL[pool]}
            note="od % obsazenosti"
            aLabel="od %"
            rows={bandsOf(pool)}
            canWrite={canWrite}
            onSave={async (steps) => {
              await api.saveBands({ pool, steps: steps.map((s) => ({ fromPct: s.a, factor: s.f })) });
              await refresh();
            }}
          />
        ))}
        <Ladder
          title="Předstih objednávky"
          note="od kolika dní před příjezdem"
          aLabel="od dní"
          rows={cfg.leads.map((l: PricingLead) => ({ a: l.fromDays, f: l.factor }))}
          canWrite={canWrite}
          onSave={async (steps) => {
            await api.saveLeads({ steps: steps.map((s) => ({ fromDays: s.a, factor: s.f })) });
            await refresh();
          }}
        />
      </div>
    </div>
  );
}

/* ---- discounts --------------------------------------------------------- */

function DiscountRow({ d, canWrite }: { d: PricingDiscount; canWrite: boolean }) {
  const qc = useQueryClient();
  const [x, setX] = useState({
    code: d.code,
    title: d.title,
    kind: d.kind,
    value: String(d.value),
    userId: d.userId == null ? '' : String(d.userId),
    products: d.products,
    validFrom: d.validFrom ?? '',
    validTill: d.validTill ?? '',
    status: d.status,
  });
  useEffect(
    () =>
      setX({
        code: d.code,
        title: d.title,
        kind: d.kind,
        value: String(d.value),
        userId: d.userId == null ? '' : String(d.userId),
        products: d.products,
        validFrom: d.validFrom ?? '',
        validTill: d.validTill ?? '',
        status: d.status,
      }),
    [d],
  );
  const m = useMutation({
    mutationFn: () =>
      api.updateDiscount(d.id, {
        code: x.code.trim(),
        title: x.title.trim(),
        kind: x.kind,
        value: Number(x.value),
        userId: x.userId ? Number(x.userId) : null,
        products: x.products,
        validFrom: x.validFrom || null,
        validTill: x.validTill || null,
        status: x.status,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings', 'pricing'] });
      await qc.invalidateQueries({ queryKey: ['quote'] });
    },
  });
  const dirty =
    x.code !== d.code ||
    x.title !== d.title ||
    x.kind !== d.kind ||
    Number(x.value) !== d.value ||
    (x.userId ? Number(x.userId) : null) !== d.userId ||
    x.products.join() !== d.products.join() ||
    (x.validFrom || null) !== d.validFrom ||
    (x.validTill || null) !== d.validTill ||
    x.status !== d.status;
  const toggle = (t: ReservationType) => setX({ ...x, products: x.products.includes(t) ? x.products.filter((p) => p !== t) : [...x.products, t] });
  return (
    <>
      <tr className={d.status !== 'ok' ? 'deleted' : undefined}>
        <td>
          <input className="short" value={x.code} maxLength={40} disabled={!canWrite} onChange={(e) => setX({ ...x, code: e.target.value.toUpperCase() })} placeholder={d.userId ? '(partner)' : 'KÓD'} />
        </td>
        <td>
          <input className="wide" value={x.title} maxLength={120} disabled={!canWrite} onChange={(e) => setX({ ...x, title: e.target.value })} />
        </td>
        <td>
          <select value={x.kind} disabled={!canWrite} onChange={(e) => setX({ ...x, kind: e.target.value as 'percent' | 'fixed' })}>
            <option value="percent">%</option>
            <option value="fixed">Kč</option>
          </select>
        </td>
        <td className="num">
          <input type="number" step={1} className="short" value={x.value} disabled={!canWrite} onChange={(e) => setX({ ...x, value: e.target.value })} />
        </td>
        <td className="num">
          <input type="number" min={1} className="short" value={x.userId} disabled={!canWrite} onChange={(e) => setX({ ...x, userId: e.target.value })} placeholder="—" />
        </td>
        <td>
          {RESERVATION_TYPES.map((t) => (
            <label key={t} className="chk" style={{ marginRight: 6 }}>
              <input type="checkbox" checked={x.products.length === 0 || x.products.includes(t)} disabled={!canWrite} onChange={() => toggle(t)} /> {TYPE_SHORT[t]}
            </label>
          ))}
        </td>
        <td>
          <input type="date" value={x.validFrom} disabled={!canWrite} onChange={(e) => setX({ ...x, validFrom: e.target.value })} />
        </td>
        <td>
          <input type="date" value={x.validTill} disabled={!canWrite} onChange={(e) => setX({ ...x, validTill: e.target.value })} />
        </td>
        <td className="num">
          <input type="checkbox" checked={x.status === 'deleted'} disabled={!canWrite} onChange={(e) => setX({ ...x, status: e.target.checked ? 'deleted' : 'ok' })} aria-label="vyřazená" />
        </td>
        <td className="act">
          {canWrite && (
            <button type="button" className="btn sm primary" disabled={!dirty || m.isPending} onClick={() => m.mutate()}>
              {m.isPending ? '…' : 'Uložit'}
            </button>
          )}
          {m.isSuccess && !dirty && <span className="arrived-mark"> ✔</span>}
        </td>
      </tr>
      {(errText(m.error) || d.note) && (
        <tr className="translations">
          <td />
          <td colSpan={9}>
            {d.note && <span className="was">{d.note}</span>}
            {errText(m.error) && (
              <span className="arrive-error" role="alert">
                {' '}
                {errText(m.error)}
              </span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DiscountsCard({ cfg, canWrite }: { cfg: PricingConfig; canWrite: boolean }) {
  const qc = useQueryClient();
  const [showRetired, setShowRetired] = useState(false);
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<'percent' | 'fixed'>('percent');
  const [value, setValue] = useState('');
  const [userId, setUserId] = useState('');
  const create = useMutation({
    mutationFn: () => api.createDiscount({ code: code.trim(), title: title.trim(), kind, value: Number(value), userId: userId ? Number(userId) : null }),
    onSuccess: async () => {
      setCode('');
      setTitle('');
      setValue('');
      setUserId('');
      await qc.invalidateQueries({ queryKey: ['settings', 'pricing'] });
    },
  });
  const rows = cfg.discounts.filter((d) => showRetired || d.status === 'ok');
  const ok = (code.trim() || userId) && Number.isInteger(Number(value)) && value !== '';
  return (
    <div className="card detailcard">
      <h2>Slevy a kódy</h2>
      <p className="settings-note">
        Uplatní se až na výslednou cenu: procenta (záporné = příplatek) nebo pevná částka. Kód zadává přepážka nebo klient; sleva s <b>partnerem</b> (číslo
        účtu) platí na jeho rezervace bez kódu. Prázdné produkty = všechny. U importovaných slev je v poznámce napsáno, odkud se procento vzalo — z
        dohodnutého čísla v kódu CK, z názvu ceníku, nebo odvozené ze 7denní ceny Venku (ta se má zkontrolovat). Vyřazené řádky jsou kódy, které
        nikdy nikdo nepoužil.
      </p>
      <div className="tablewrap sestavawrap">
        <table className="mini settings discounts">
          <thead>
            <tr>
              <th>Kód</th>
              <th>Název</th>
              <th>Druh</th>
              <th className="num">Hodnota</th>
              <th className="num">Partner #</th>
              <th>Produkty</th>
              <th>Od</th>
              <th>Do</th>
              <th className="num">Vyřazená</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <DiscountRow key={d.id} d={d} canWrite={canWrite} />
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="empty">
                  Žádná sleva.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="efactions">
        <label className="chk">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> i vyřazené
        </label>
      </div>
      {canWrite && (
        <form
          className="editform"
          style={{ marginTop: 10 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (ok && !create.isPending) create.mutate();
          }}
        >
          <div className="efgrid">
            <label className="ef">
              Nový kód
              <input value={code} maxLength={40} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="např. ZIMA15" />
            </label>
            <label className="ef">
              Název
              <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="ef">
              Druh a hodnota
              <span style={{ display: 'flex', gap: 6 }}>
                <select value={kind} onChange={(e) => setKind(e.target.value as 'percent' | 'fixed')}>
                  <option value="percent">%</option>
                  <option value="fixed">Kč</option>
                </select>
                <input type="number" step={1} value={value} onChange={(e) => setValue(e.target.value)} style={{ width: 90 }} />
              </span>
            </label>
            <label className="ef">
              Nebo partner (číslo účtu)
              <input type="number" min={1} value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="—" />
            </label>
          </div>
          <div className="efactions">
            <button type="submit" className="btn primary" disabled={!ok || create.isPending}>
              {create.isPending ? 'Zakládám…' : 'Přidat slevu'}
            </button>
            {errText(create.error) && (
              <span className="arrive-error" role="alert">
                {errText(create.error)}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

/* ---- the sandbox -------------------------------------------------------- */

function Sandbox() {
  const [arrival, setArrival] = useState(`${todayISO(7)}T10:00`);
  const [departure, setDeparture] = useState(`${todayISO(14)}T10:00`);
  const [type, setType] = useState<ReservationType>('cheap');
  const [code, setCode] = useState('');
  const key = { arrival: arrival.replace('T', ' '), departure: departure.replace('T', ' '), type, promo: code.trim() || undefined };
  const ok = arrival.length >= 16 && departure.length >= 16 && departure > arrival;
  const v2 = useQuery({ queryKey: ['quote', 'sandbox', 'v2', key], queryFn: () => api.quote({ ...key, dynamic: true, engine: 'v2' }), enabled: ok });
  const legacy = useQuery({ queryKey: ['quote', 'sandbox', 'legacy', key], queryFn: () => api.quote({ ...key, dynamic: true, engine: 'legacy' }), enabled: ok });
  return (
    <div className="card detailcard">
      <h2>Vyzkoušet cenu</h2>
      <p className="settings-note">Stejný termín oběma systémy — nic se neukládá.</p>
      <div className="efgrid">
        <label className="ef">
          Příjezd
          <input type="datetime-local" value={arrival} onChange={(e) => setArrival(e.target.value)} />
        </label>
        <label className="ef">
          Odjezd
          <input type="datetime-local" value={departure} onChange={(e) => setDeparture(e.target.value)} />
        </label>
        <label className="ef">
          Produkt
          <select value={type} onChange={(e) => setType(e.target.value as ReservationType)}>
            {RESERVATION_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_SHORT[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="ef">
          Kód
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="nepovinné" />
        </label>
      </div>
      <div className="sandbox">
        <div>
          <h3 className="efh">Nový systém</h3>
          {v2.isError ? (
            <span className="arrive-error">{errText(v2.error) ?? 'Nelze spočítat — chybí základní sezóna?'}</span>
          ) : v2.data ? (
            <>
              <div className="big">{CZK.format(v2.data.price)} Kč</div>
              <QuoteBreakdown q={v2.data} />
            </>
          ) : (
            <span className="empty">…</span>
          )}
        </div>
        <div>
          <h3 className="efh">Starý systém (ceníky, jako web)</h3>
          {legacy.data ? (
            <>
              <div className="big">{CZK.format(legacy.data.price)} Kč</div>
              <span className="was">
                ceník #{legacy.data.pricelistId} {legacy.data.pricelistTitle} · základ {CZK.format(legacy.data.base)} × {legacy.data.factor}
                {legacy.data.remaining != null ? ` (volných ${legacy.data.remaining})` : ''}
              </span>
            </>
          ) : (
            <span className="empty">…</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- the screen ---------------------------------------------------------- */

export function Ceny() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const canWrite = can(me.data, '/settings/*', 'write');
  const q = useQuery({ queryKey: ['settings', 'pricing'], queryFn: () => api.pricing() });
  return (
    <div className="container">
      <div className="pagehead">
        <h1>Dynamické ceny</h1>
        <span className="sub">{q.data ? (q.data.settings.engine === 'v2' ? 'přepážka počítá novým systémem' : 'přepážka počítá z ceníků (starý systém)') : '…'}</span>
        <div className="acts">
          <Link to="/nastaveni" className="btn sm">
            ← Nastavení
          </Link>
        </div>
      </div>
      {q.isPending ? (
        <div className="card state">Načítám…</div>
      ) : q.isError ? (
        <div className="card state" role="alert">
          Nastavení cen se nepodařilo načíst.
        </div>
      ) : (
        <>
          <SettingsCard cfg={q.data} canWrite={canWrite} />
          {!q.data.settings.empty && (
            <>
              <SeasonsCard cfg={q.data} canWrite={canWrite} />
              <LaddersCard cfg={q.data} canWrite={canWrite} />
              <DiscountsCard cfg={q.data} canWrite={canWrite} />
              <Sandbox />
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ---- one season's table --------------------------------------------------- */

export function SezonaDetail() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const canWrite = can(me.data, '/settings/*', 'write');
  const q = useQuery({ queryKey: ['settings', 'pricing'], queryFn: () => api.pricing() });
  const s = q.data?.seasons.find((x) => x.id === id) ?? null;
  const [d, setD] = useState<{ title: string; validFrom: string; validTill: string; priority: string; status: 'ok' | 'deleted'; note: string; rates: Record<ReservationType, string[] | null> } | null>(null);
  useEffect(() => {
    if (s) {
      setD({
        title: s.title,
        validFrom: s.validFrom ?? '',
        validTill: s.validTill ?? '',
        priority: String(s.priority),
        status: s.status,
        note: s.note,
        rates: { cheap: s.rates.cheap?.map(String) ?? null, regular: s.rates.regular?.map(String) ?? null, exclusive: s.rates.exclusive?.map(String) ?? null },
      });
    }
  }, [s]);
  const save = useMutation({
    mutationFn: () => {
      if (!d || !s) throw new Error('nic');
      const rates: Partial<Record<ReservationType, number[] | null>> = {};
      for (const t of RESERVATION_TYPES) {
        const cur = d.rates[t];
        const was = s.rates[t];
        if ((cur == null) !== (was == null) || (cur && was && cur.some((v, i) => Number(v) !== was[i]))) rates[t] = cur ? cur.map((v) => Number(v)) : null;
      }
      return api.updateSeason(id, {
        title: d.title.trim(),
        validFrom: d.validFrom || null,
        validTill: d.validTill || null,
        priority: Number(d.priority) || 0,
        status: d.status,
        note: d.note.trim(),
        rates,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings', 'pricing'] });
      await qc.invalidateQueries({ queryKey: ['quote'] });
    },
  });
  if (q.isPending || !d || !s) return <div className="container"><div className="card state">{q.isPending ? 'Načítám…' : 'Sezóna nenalezena.'}</div></div>;
  const valid =
    d.title.trim().length > 0 &&
    (d.validFrom === '') === (d.validTill === '') &&
    (!d.validFrom || d.validFrom <= d.validTill) &&
    RESERVATION_TYPES.every((t) => !d.rates[t] || d.rates[t]!.every((v) => Number.isInteger(Number(v)) && Number(v) >= 0));
  const setCol = (t: ReservationType, i: number, v: string) => setD({ ...d, rates: { ...d.rates, [t]: d.rates[t]!.map((x, j) => (j === i ? v : x)) } });
  const fillDown = (t: ReservationType, i: number) => {
    const arr = [...d.rates[t]!];
    const cur = Number(arr[i]);
    const step = i > 0 ? cur - Number(arr[i - 1]) : cur;
    for (let j = i + 1; j < PRICELIST_DAYS - 1; j += 1) arr[j] = String(Math.max(0, cur + step * (j - i)));
    setD({ ...d, rates: { ...d.rates, [t]: arr } });
  };
  const toggleProduct = (t: ReservationType) =>
    setD({ ...d, rates: { ...d.rates, [t]: d.rates[t] ? null : (q.data!.seasons.find((x) => x.validFrom == null)?.rates[t] ?? new Array(PRICELIST_DAYS).fill(0)).map(String) } });

  return (
    <div className="container">
      <div className="pagehead">
        <h1>{s.title}</h1>
        <span className="sub">
          sezóna #{s.id} · {s.validFrom ? `${czDay(s.validFrom)} – ${czDay(s.validTill)}` : 'základní'}
        </span>
        <div className="acts">
          <Link to="/nastaveni/ceny" className="btn sm">
            ← Dynamické ceny
          </Link>
        </div>
      </div>
      <form
        className="card detailcard"
        onSubmit={(e) => {
          e.preventDefault();
          if (canWrite && valid && !save.isPending) save.mutate();
        }}
      >
        <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="efgrid">
            <label className="ef">
              Název
              <input value={d.title} maxLength={80} required onChange={(e) => setD({ ...d, title: e.target.value })} />
            </label>
            <label className="ef">
              Od (prázdné = základní)
              <input type="date" value={d.validFrom} onChange={(e) => setD({ ...d, validFrom: e.target.value })} />
            </label>
            <label className="ef">
              Do
              <input type="date" value={d.validTill} onChange={(e) => setD({ ...d, validTill: e.target.value })} />
            </label>
            <label className="ef">
              Priorita
              <input type="number" min={-100} max={100} value={d.priority} onChange={(e) => setD({ ...d, priority: e.target.value })} />
            </label>
            <label className="ef">
              Stav
              <select value={d.status} onChange={(e) => setD({ ...d, status: e.target.value as 'ok' | 'deleted' })}>
                <option value="ok">živá</option>
                <option value="deleted">vyřazená</option>
              </select>
            </label>
            <label className="ef wide2">
              Poznámka
              <input value={d.note} maxLength={2000} onChange={(e) => setD({ ...d, note: e.target.value })} />
            </label>
          </div>
          <h3 className="efh">Ceny podle délky pobytu (Kč celkem za pobyt)</h3>
          <p className="settings-note">
            Řádek 32 je každý další den nad 31. Produkt bez vlastní tabulky bere ceny ze základní sezóny; tlačítko ↓ doplní dny pod sebou stejným krokem.
          </p>
          <div className="efactions">
            {RESERVATION_TYPES.map((t) => (
              <label key={t} className="chk">
                <input type="checkbox" checked={!!d.rates[t]} onChange={() => toggleProduct(t)} /> {TYPE_SHORT[t]} má vlastní ceny
              </label>
            ))}
          </div>
          <div className="tablewrap sestavawrap">
            <table className="mini settings cenik">
              <thead>
                <tr>
                  <th>Den</th>
                  {RESERVATION_TYPES.map((t) => (
                    <th key={t} className="num" colSpan={2}>
                      {TYPE_SHORT[t]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: PRICELIST_DAYS }, (_, i) => (
                  <tr key={i} className={i === PRICELIST_DAYS - 1 ? 'extra' : undefined}>
                    <td>{i === PRICELIST_DAYS - 1 ? 'každý další den' : `${i + 1}`}</td>
                    {RESERVATION_TYPES.map((t) => (
                      <Fragment key={t}>
                        <td className="num">
                          {d.rates[t] ? (
                            <input type="number" min={0} step={1} value={d.rates[t]![i]} onChange={(e) => setCol(t, i, e.target.value)} aria-label={`${TYPE_SHORT[t]} — den ${i + 1}`} />
                          ) : (
                            <span className="was">základní</span>
                          )}
                        </td>
                        <td className="act">
                          {d.rates[t] && i < PRICELIST_DAYS - 2 && canWrite && (
                            <button type="button" className="btn sm" title="Doplnit dny pod sebou stejným krokem" onClick={() => fillDown(t, i)}>
                              ↓
                            </button>
                          )}
                        </td>
                      </Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </fieldset>
        <div className="efactions">
          {canWrite && (
            <button type="submit" className="btn primary" disabled={!valid || save.isPending}>
              {save.isPending ? 'Ukládám…' : 'Uložit sezónu'}
            </button>
          )}
          {save.isSuccess && <span className="arrived-mark">✔ Uloženo</span>}
          {errText(save.error) && (
            <span className="arrive-error" role="alert">
              {errText(save.error)}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
